import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiX, FiExternalLink } from 'react-icons/fi';
import './ProjectModal.css';

const ProjectModal = ({ project, gradient, IconComponent, onClose }) => {
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    // Focus close button after Framer Motion entrance animation
    requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    // iOS-safe body scroll lock
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key === 'Tab') {
        // Query focusable elements on each Tab press (not cached)
        const focusableElements = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
    };
  }, [onClose]);

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        className="modal-panel glass-panel"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Banner */}
        <div className="modal-banner" style={{ background: gradient }}>
          <div className="modal-banner-inner">
            <IconComponent size={64} aria-hidden="true" />
          </div>
        </div>

        {/* Close button */}
        <motion.button
          className="modal-close-btn"
          ref={closeButtonRef}
          onClick={onClose}
          aria-label="Close project details"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <FiX size={20} />
        </motion.button>

        {/* Body */}
        <div className="modal-body">
          <h3 id="modal-title" className="modal-title">{project.title}</h3>
          <p className="modal-role">{project.role}</p>
          <p className="modal-description">{project.description}</p>
          <div className="modal-badges">
            {project.techStack.map((tech) => (
              <span key={tech} className="tech-badge">{tech}</span>
            ))}
          </div>
          {project.liveUrl ? (
            <a
              href={project.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="skeuo-button small-btn modal-action-btn"
              aria-label={`View live demo of ${project.title}`}
              onClick={(e) => e.stopPropagation()}
            >
              View Live <FiExternalLink size={14} />
            </a>
          ) : (
            <span className="self-hosted-indicator">Self-hosted</span>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ProjectModal;
